import {
  Button,
  Link,
  Page,
  PageBody,
  PageHeader,
  PageHeaderTitle,
  PageHeaderActions,
  PageHeaderDescription,
  Banner,
  Stack,
  Spinner,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { Navigate } from '@/utils/navigate';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { AIBusinessProfileWrapper } from './components';
import { useAIContextOverrides } from './hooks';
import { isAPIError } from '@/services/api/ts-rest';
import { useAnalytics } from '@/services/analytics';
import { usePageViewTracking } from '@/hooks/use-page-view-tracking';
import { countFieldStats } from './helpers/form';
import { PageTitle } from '@/components/page-title';
import { generatePath } from '@/services/router/generate-path';
import { useRegisterBreadcrumbs } from '@/services/breadcrumbs';

const AIBusinessProfilePageLayout = ({ children }: { children: React.ReactNode }) => {
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const breadcrumbs = [
    {
      id: 'workspace_settings',
      text: 'Workspace settings',
      href: generatePath('/workspaces/:workspaceId/settings', { workspaceId: workspaceId ?? 'last' }),
    },
    {
      id: 'ai_business_profile',
      text: 'Business context',
      href: generatePath('/workspaces/:workspaceId/settings/ai-business-profile', {
        workspaceId: workspaceId ?? 'last',
      }),
    },
  ];
  useRegisterBreadcrumbs(breadcrumbs, 'root');

  return (
    <>
      <PageTitle title="Business context" />
      <Page style={{ width: '100%' }}>{children}</Page>
    </>
  );
};

export const AIBusinessProfile = () => {
  const { hasPermission } = usePermissions();
  const { data, isLoading, error, refetch } = useAIContextOverrides();
  const formId = 'ai-business-profile-form';
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const { track } = useAnalytics();

  usePageViewTracking(() => {
    const stats = countFieldStats(data?.ai_context, data?.overrides);
    track('aiProfile', 'viewed', {
      'Fields Total': stats.total,
      'Fields AI Populated': stats.aiPopulated,
      'Fields User Overridden': stats.userOverridden,
      'Fields Empty': stats.empty,
    });
  }, data != null);

  if (hasPermission('journeys/workspace_settings/view') !== true) {
    return (
      <Navigate
        to={generatePath('/workspaces/:workspaceId/settings', { workspaceId: workspaceId ?? 0 })}
        replace={true}
      />
    );
  }

  const canEdit = hasPermission('journeys/workspace_settings/edit') === true;

  if (error != null) {
    return (
      <AIBusinessProfilePageLayout>
        <PageBody>
          <ErrorState error={error} onRetry={refetch} />
        </PageBody>
      </AIBusinessProfilePageLayout>
    );
  }

  if (isLoading === true) {
    return (
      <AIBusinessProfilePageLayout>
        <PageBody>
          <Stack alignBlock="center" alignInline="center" mt="3200">
            <Spinner />
          </Stack>
        </PageBody>
      </AIBusinessProfilePageLayout>
    );
  }

  if (data == null) {
    return null;
  }

  return (
    <AIBusinessProfilePageLayout>
      <AIBusinessProfileWrapper
        aiContext={data.ai_context}
        overrides={data.overrides}
        formId={formId}
        canEdit={canEdit}
        onEditIntent={(fieldName) => {
          track('aiProfile', 'editIntent', {
            Field: fieldName,
          });
        }}
        onSave={(changeInfo) => {
          track('aiProfile', 'saved', {
            'Fields Changed Count': changeInfo.fieldsChangedCount,
            'Fields Added Count': changeInfo.fieldsAddedCount,
            'Fields Edited Count': changeInfo.fieldsEditedCount,
            'Fields Cleared Count': changeInfo.fieldsClearedCount,
            'Changed Fields': changeInfo.changedFields,
            'Edit Session Ms': changeInfo.editSessionMs,
          });
        }}
        onDiscard={(unsavedFieldsChangedCount) => {
          track('aiProfile', 'discarded', {
            'Unsaved Fields Changed Count': unsavedFieldsChangedCount,
          });
        }}
      >
        {(formState) => (
          <>
            <PageHeader>
              <PageHeaderTitle>Business context</PageHeaderTitle>
              {canEdit ? (
                <PageHeaderActions>
                  <Button
                    variant="primary"
                    type="submit"
                    form={formId}
                    isDisabled={formState.isDirty !== true || formState.isValid !== true}
                  >
                    Save
                  </Button>
                </PageHeaderActions>
              ) : null}
              <PageHeaderDescription>
                Provide context about your business to help AI features deliver more relevant, on-brand content.{' '}
                <Link href="https://docs.customer.io/ai/cio-with-llms/" variant="secondary">
                  Learn more about Customer.io's AI philosophy
                </Link>
              </PageHeaderDescription>
            </PageHeader>
            <PageBody pb="1000">{formState.formContent}</PageBody>
          </>
        )}
      </AIBusinessProfileWrapper>
    </AIBusinessProfilePageLayout>
  );
};

const ErrorState = ({ error, onRetry }: { error: unknown; onRetry: () => unknown }) => {
  const getErrorMessage = (error: unknown): string => {
    if (isAPIError(error)) {
      const body = error.response.body as Record<string, unknown>;
      if (typeof body === 'object' && body !== null) {
        const errors = body.errors as Array<{ detail?: string }> | undefined;
        if (errors?.[0]?.detail != null) {
          return errors[0].detail;
        }
        const message = body.message;
        if (typeof message === 'string') {
          return message;
        }
      }
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'An unexpected error occurred while loading your Business context.';
  };

  return (
    <Stack>
      <Banner
        variant="error"
        title="Unable to load Business context"
        action={{
          label: 'Try again',
          onClick: onRetry,
          variant: 'subtle',
        }}
      >
        {getErrorMessage(error)}
      </Banner>
    </Stack>
  );
};
